import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Copy, Check } from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { apiService } from '../services/apiService';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [emailCopied, setEmailCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Using the portal reset password endpoint
      const response = await apiService.get(`/crm/auth/reset-pw?username=${encodeURIComponent(email.trim())}`);
      console.log('Password reset response:', response);
      setSuccess(true);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText('support@itp360.com');
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Check your email</h2>
            <p className="text-gray-600 mb-6">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors mb-4"
              style={{background: 'linear-gradient(to right, #4834d4, #341f97)'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #341f97, #2a1a7a)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #4834d4, #341f97)';
              }}
            >
              Back to Sign In
            </Link>
            
            <div className="border-t border-gray-100 pt-5 mt-4">
              <div className="text-center mb-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Need Assistance?</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <a 
                  href="tel:888-274-4529" 
                  className="group flex flex-col items-center p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md"
                >
                  <div className="w-12 h-12 bg-slate-100 group-hover:bg-slate-200 rounded-full flex items-center justify-center mb-3 transition-all">
                    <svg className="h-6 w-6 text-slate-600 group-hover:text-slate-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 mb-1">Phone Support</span>
                  <span className="text-xs text-gray-500 font-mono">888-274-4529</span>
                </a>
                <div className="group flex flex-col items-center p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md">
                  <div className="w-12 h-12 bg-slate-100 group-hover:bg-slate-200 rounded-full flex items-center justify-center mb-3 transition-all">
                    <svg className="h-6 w-6 text-slate-600 group-hover:text-slate-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 mb-1">Email Support</span>
                  <div className="flex items-center space-x-2">
                    <a 
                      href="mailto:support@itp360.com" 
                      className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      support@itp360.com
                    </a>
                    <button
                      onClick={copyEmail}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all"
                      title="Copy email address"
                    >
                      {emailCopied ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Welcome */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center relative overflow-hidden" style={{background: 'linear-gradient(to right, #4834d4, #341f97)'}}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="text-center text-white relative z-10">
          <img 
            src="/logoBlack.png" 
            alt="ITP360" 
            className="h-16 w-auto mx-auto mb-8 filter brightness-0 invert"
          />
          <h1 className="text-4xl font-bold mb-4">Forgot your password?</h1>
          <p className="text-white text-xl font-light opacity-90">
            No worries, we'll help you reset it
          </p>
        </div>
      </div>

      {/* Right side - Reset form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logoBlack.png" 
              alt="ITP360" 
              className="h-12 w-auto mx-auto mb-6"
            />
            <h1 className="text-2xl font-bold text-gray-900">Reset Password</h1>
          </div>

          <div className="hidden lg:block mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Reset Password</h2>
            <p className="text-gray-600 mt-2">Enter your email to receive a reset link</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your email address"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                background: 'linear-gradient(to right, #4834d4, #341f97)',
                "--tw-ring-color": "#4834d4"
              } as React.CSSProperties & {"--tw-ring-color": string}}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #341f97, #2a1a7a)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, #4834d4, #341f97)';
              }}
            >
              {loading ? (
                <LoadingSpinner size="small" className="text-white" />
              ) : (
                'Send Reset Link'
              )}
            </button>

            <div className="text-center space-y-3">
              <Link
                to="/login"
                className="text-sm font-medium transition-colors block"
                style={{color: '#4834d4'}}
                onMouseEnter={(e) => e.currentTarget.style.color = '#341f97'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4834d4'}
              >
                Back to Sign In
              </Link>
              
              <div className="border-t border-gray-100 pt-5 mt-4">
                <div className="text-center mb-3">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Support Center</h4>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href="tel:888-274-4529" 
                    className="group flex flex-col items-center p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md"
                  >
                    <div className="w-12 h-12 bg-slate-100 group-hover:bg-slate-200 rounded-full flex items-center justify-center mb-3 transition-all">
                      <svg className="h-6 w-6 text-slate-600 group-hover:text-slate-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 mb-1">Phone Support</span>
                    <span className="text-xs text-gray-500 font-mono">888-274-4529</span>
                  </a>
                  <div className="group flex flex-col items-center p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md">
                    <div className="w-12 h-12 bg-slate-100 group-hover:bg-slate-200 rounded-full flex items-center justify-center mb-3 transition-all">
                      <svg className="h-6 w-6 text-slate-600 group-hover:text-slate-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 mb-1">Email Support</span>
                    <div className="flex items-center space-x-2">
                      <a 
                        href="mailto:support@itp360.com" 
                        className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        support@itp360.com
                      </a>
                      <button
                        onClick={copyEmail}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all"
                        title="Copy email address"
                      >
                        {emailCopied ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
